import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Eye,
  ChevronLeft,
  ChevronDown,
  Users,
  CheckCircle,
  XCircle,
  Clock3,
  BarChart4,
  Award,
  Calendar,
  Search,
  Save,
  RefreshCw,
  Edit3,
  Share2,
  ArrowLeft,
  Clock,
  Loader2,
  Bot,
  Download,
  UserPlus,
  FileText,
  Eye as EyeIcon,
} from "lucide-react";
import {
  useGetEssayAssignment,
  useGetEssayGrader,
  useUpdateGrade,
  useReleaseGrades,
  useGetSubmissionsForAssignment,
  useBulkUpdateGrades,
  useAssignStudents,
} from "@packages/api/madlenAPI";
import {
  StudentEssaySubmissionResponseDto,
  ReleaseGradesRequestDto,
  UpdateGradeRequestDto,
  GradeUpdateItem,
  BulkGradeUpdateRequestDto,
} from "@packages/api/madlenAPI.schemas";
import { useToast } from "@packages/contexts/ToastContext";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Text } from "@core/index";
import { ChatMessageBox } from "@features/assistants/components/essay-grader/ChatMessageBox";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ClassroomAndStudentSelector } from "@features/assignments/SingleAssignment/componnets/ClassroomAndStudentSelector";

const InlineLoader: React.FC<{ className?: string; size?: number }> = ({
  className = "",
  size = 16,
}) => (
  <div className={`inline-flex items-center ${className}`}>
    <Loader2 className={`animate-spin`} size={size} />
  </div>
);

const EssayDetailLoader: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <div className="relative">
            <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          </div>

          <div className="space-y-3">
            <Text
              variant="h2"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {t("essayAssignments.loading")}
            </Text>
            <Text
              variant="p"
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              {t("essayAssignments.loadingDescription")}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

// FIXED: getMaxScoreForSubmission - No default fallback, returns 0 if no valid max score
const getMaxScoreForSubmission = (
  submission: StudentEssaySubmissionResponseDto,
  defaultMaxScore?: number,
): number => {
  // First check direct maxScore
  if (submission.maxScore && submission.maxScore > 0) {
    return submission.maxScore;
  }

  // Then check student essay result max score
  if (
    submission.studentEssayResult?.max_score &&
    submission.studentEssayResult.max_score > 0
  ) {
    return submission.studentEssayResult.max_score;
  }

  // Calculate from criteria evaluations if available
  if (
    submission.studentEssayResult?.criteria_evaluations &&
    submission.studentEssayResult.criteria_evaluations.length > 0
  ) {
    const totalMaxScore =
      submission.studentEssayResult.criteria_evaluations.reduce(
        (sum, criterion) => {
          return sum + (criterion.max_score || 0);
        },
        0,
      );
    if (totalMaxScore > 0) {
      return totalMaxScore;
    }
  }

  // Use provided default max score only if valid
  if (defaultMaxScore && defaultMaxScore > 0) {
    return defaultMaxScore;
  }

  // FIXED: Return 0 instead of 100 as fallback
  return 0;
};

const getDynamicRadarData = (submission: StudentEssaySubmissionResponseDto) => {
  const essayResult = submission.studentEssayResult;

  if (
    !essayResult?.criteria_evaluations ||
    essayResult.criteria_evaluations.length === 0
  ) {
    const maxScore = getMaxScoreForSubmission(submission);
    const currentScore = submission.score || 0;

    return [
      {
        subject: "Genel Kalite",
        A: currentScore,
        fullMark: maxScore,
      },
    ];
  }

  return essayResult.criteria_evaluations.map((criterion) => ({
    subject: criterion.criterion_name,
    A: criterion.score,
    fullMark: criterion.max_score,
  }));
};

const shouldShowRadarChart = (radarData: any[]) => {
  return radarData.some((item) => item.A > 0) && radarData.length > 2;
};

const isDueDatePassed = (dueDate: string | null | undefined): boolean => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

const getSubmissionStatus = (
  submission: StudentEssaySubmissionResponseDto,
  t: (key: string) => string,
  dueDatePassed: boolean,
) => {
  const isAIGrading =
    submission.studentEssayResult === null &&
    submission.status === "SUBMITTED" &&
    submission.essayFiles &&
    submission.essayFiles.length > 0;

  if (isAIGrading) {
    return {
      status: "AI_GRADING",
      label: t("essayAssignments.status.aiGrading") || "AI Analiz Ediyor",
      icon: <Bot className="w-3 h-3 animate-pulse" />,
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      textColor: "text-orange-700 dark:text-orange-300",
    };
  }

  switch (submission.status) {
    case "NOT_SUBMITTED":
      return {
        status: "NOT_SUBMITTED",
        label: t("essayAssignments.status.notSubmitted") || "Teslim Edilmedi",
        icon: <XCircle className="w-3 h-3" />,
        bgColor: dueDatePassed
          ? "bg-red-50 dark:bg-red-900/20"
          : "bg-gray-50 dark:bg-gray-900/20",
        textColor: dueDatePassed
          ? "text-red-700 dark:text-red-300"
          : "text-gray-700 dark:text-gray-300",
      };

    case "SUBMITTED":
      return {
        status: "SUBMITTED",
        label: t("essayAssignments.status.submitted") || "Teslim Edildi",
        icon: <CheckCircle className="w-3 h-3" />,
        bgColor: "bg-green-50 dark:bg-green-900/20",
        textColor: "text-green-700 dark:text-green-300",
      };

    case "GRADED":
      return {
        status: "GRADED",
        label: t("essayAssignments.status.graded") || "Değerlendirildi",
        icon: <Award className="w-3 h-3" />,
        bgColor: "bg-purple-50 dark:bg-purple-900/20",
        textColor: "text-purple-700 dark:text-purple-300",
      };

    case "GRADE_VISIBLE":
      return {
        status: "GRADE_VISIBLE",
        label: t("essayAssignments.status.gradeVisible") || "Not Görünür",
        icon: <Eye className="w-3 h-3" />,
        bgColor: "bg-blue-50 dark:bg-blue-900/20",
        textColor: "text-blue-700 dark:text-blue-300",
      };

    default:
      return {
        status: "NOT_SUBMITTED",
        label: t("essayAssignments.status.notSubmitted") || "Teslim Edilmedi",
        icon: <XCircle className="w-3 h-3" />,
        bgColor: dueDatePassed
          ? "bg-red-50 dark:bg-red-900/20"
          : "bg-gray-50 dark:bg-gray-900/20",
        textColor: dueDatePassed
          ? "text-red-700 dark:text-red-300"
          : "text-gray-700 dark:text-gray-300",
      };
  }
};

interface ScoreSliderProps {
  initialScore?: number;
  maxScore?: number;
  onSave: (score: number) => void;
  disabled?: boolean;
}

const ScoreSlider: React.FC<ScoreSliderProps> = ({
  initialScore = 0,
  maxScore = 0,
  onSave,
  disabled = false,
}) => {
  const [score, setScore] = useState(initialScore);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (score === initialScore) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(score);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setScore(initialScore);
    setIsEditing(false);
  };

  const hasValidMaxScore = maxScore > 0;

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        {initialScore > 0 && hasValidMaxScore ? (
          <div className="bg-blue-100 dark:bg-blue-900/20 px-3 py-1 rounded-full">
            <Text
              variant="span"
              className="text-sm font-bold text-blue-800 dark:text-blue-200"
            >
              {initialScore}/{maxScore}
            </Text>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
        {!disabled && hasValidMaxScore && (
          <Button
            onClick={() => setIsEditing(true)}
            size="sm"
            rounded="lg"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-gray-600 dark:text-gray-300"
            className="p-1"
          >
            <Edit3 className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={maxScore}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          disabled={isSaving}
        />
        <span className="text-sm font-medium min-w-[50px]">
          {score}/{maxScore}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          onClick={handleSave}
          size="sm"
          rounded="lg"
          bgColor="bg-green-600 hover:bg-green-700"
          textColor="text-white"
          className="p-1"
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Save className="w-3 h-3" />
          )}
        </Button>
        <Button
          onClick={handleCancel}
          size="sm"
          rounded="lg"
          bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          textColor="text-gray-600 dark:text-gray-300"
          className="p-1"
          disabled={isSaving}
        >
          <XCircle className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Enhanced FeedbackTextArea with better score integration
interface FeedbackTextAreaProps {
  initialFeedback?: string;
  initialScore?: number;
  maxScore?: number;
  onSave: (feedback: string, score?: number) => void;
  disabled?: boolean;
  showScoreInput?: boolean;
}

const FeedbackTextArea: React.FC<FeedbackTextAreaProps> = ({
  initialFeedback = "",
  initialScore = 0,
  maxScore = 0,
  onSave,
  disabled = false,
  showScoreInput = true,
}) => {
  const [feedback, setFeedback] = useState(initialFeedback);
  const [score, setScore] = useState(initialScore);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  const hasChanges =
    feedback.trim() !== initialFeedback.trim() ||
    (showScoreInput && score !== initialScore);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (showScoreInput) {
        await onSave(feedback, score);
      } else {
        await onSave(feedback);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasValidMaxScore = maxScore > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Text
          variant="h4"
          className="text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {t("essayAssignments.feedback.title") || "Geri Bildirim"}
        </Text>
        {showScoreInput && hasValidMaxScore && (
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={maxScore}
              value={score}
              onChange={(e) => setScore(Number(e.target.value))}
              className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              disabled={disabled || isSaving}
            />
            <span className="text-sm font-medium min-w-[50px]">
              {score}/{maxScore}
            </span>
          </div>
        )}
      </div>

      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows={4}
        placeholder={
          t("essayAssignments.feedback.placeholder") ||
          "Öğrenci için geri bildirim girin..."
        }
        disabled={disabled || isSaving}
      />

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          size="sm"
          rounded="lg"
          bgColor="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
          textColor="text-white"
          className="flex items-center"
        >
          {isSaving ? (
            <div className="flex items-center">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t("common.saving") || "Kaydediliyor"}
            </div>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t("common.save") || "Kaydet"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Enhanced BulkGradingModal with proper scoring support
interface BulkGradingModalProps {
  isOpen: boolean;
  onClose: () => void;
  submissions: StudentEssaySubmissionResponseDto[];
  onBulkSave: (updates: GradeUpdateItem[]) => void;
  defaultMaxScore?: number;
}

const BulkGradingModal: React.FC<BulkGradingModalProps> = ({
  isOpen,
  onClose,
  submissions,
  onBulkSave,
  defaultMaxScore,
}) => {
  const [bulkUpdates, setBulkUpdates] = useState<{
    [key: number]: GradeUpdateItem;
  }>({});
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && submissions.length > 0) {
      const initialUpdates: { [key: number]: GradeUpdateItem } = {};
      submissions.forEach((submission) => {
        if (submission.id) {
          initialUpdates[submission.id] = {
            submissionId: submission.id,
            feedback:
              submission.feedback ||
              submission.studentEssayResult?.student_feedback ||
              "",
            score:
              submission.score || submission.studentEssayResult?.score || 0,
          };
        }
      });
      setBulkUpdates(initialUpdates);
    }
  }, [isOpen, submissions]);

  const handleUpdateSubmission = (
    submissionId: number,
    field: "feedback" | "score",
    value: string | number,
  ) => {
    setBulkUpdates((prev) => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = Object.values(bulkUpdates).filter((update) => {
        const originalSubmission = submissions.find(
          (s) => s.id === update.submissionId,
        );
        const feedbackChanged =
          (update.feedback || "").trim() !==
          (
            originalSubmission?.feedback ||
            originalSubmission?.studentEssayResult?.student_feedback ||
            ""
          ).trim();
        const scoreChanged =
          (update.score || 0) !==
          (originalSubmission?.score ||
            originalSubmission?.studentEssayResult?.score ||
            0);
        return feedbackChanged || scoreChanged;
      });

      if (updates.length === 0) {
        onClose();
        return;
      }

      await onBulkSave(updates);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Text variant="h2" className="text-xl font-semibold">
              {t("essayAssignments.bulkGrading.title") || "Toplu Değerlendirme"}
            </Text>
            <Button
              onClick={onClose}
              size="sm"
              rounded="lg"
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              textColor="text-gray-500 dark:text-gray-400"
            >
              <XCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-96">
          <div className="space-y-4">
            {submissions.length === 0 ? (
              <div className="text-center py-8">
                <Text
                  variant="h3"
                  className="text-gray-500 dark:text-gray-400 mb-2"
                >
                  {t("essayAssignments.bulkGrading.noSubmissions") ||
                    "Değerlendirilebilecek kompozisyon yok"}
                </Text>
                <Text variant="p" className="text-gray-400 dark:text-gray-500">
                  {t("essayAssignments.bulkGrading.noSubmissionsDescription") ||
                    "Henüz değerlendirilebilecek kompozisyon bulunmuyor"}
                </Text>
              </div>
            ) : (
              submissions.map((submission) => {
                if (!submission.id) return null;
                const maxScore = getMaxScoreForSubmission(
                  submission,
                  defaultMaxScore,
                );
                const hasValidMaxScore = maxScore > 0;
                const update = bulkUpdates[submission.id] || {
                  submissionId: submission.id,
                  feedback:
                    submission.feedback ||
                    submission.studentEssayResult?.student_feedback ||
                    "",
                  score:
                    submission.score ||
                    submission.studentEssayResult?.score ||
                    0,
                };

                return (
                  <div
                    key={submission.id}
                    className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <Text
                        variant="h4"
                        className="font-medium text-gray-900 dark:text-white"
                      >
                        {submission.studentName ||
                          `Öğrenci ${submission.studentId}`}
                      </Text>
                      {hasValidMaxScore && (
                        <div className="flex items-center gap-2">
                          <Text
                            variant="span"
                            className="text-sm text-gray-600 dark:text-gray-400"
                          >
                            {t("essayAssignments.score.title") || "Puan"}:
                          </Text>
                          <input
                            type="range"
                            min={0}
                            max={maxScore}
                            value={update.score || 0}
                            onChange={(e) =>
                              handleUpdateSubmission(
                                submission.id!,
                                "score",
                                Number(e.target.value),
                              )
                            }
                            className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                          <span className="text-sm font-medium min-w-[50px]">
                            {update.score || 0}/{maxScore}
                          </span>
                        </div>
                      )}
                    </div>

                    <textarea
                      value={update.feedback || ""}
                      onChange={(e) =>
                        handleUpdateSubmission(
                          submission.id!,
                          "feedback",
                          e.target.value,
                        )
                      }
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder={
                        t("essayAssignments.feedback.placeholder") ||
                        "Öğrenci için geri bildirim girin..."
                      }
                    />
                    {submission.studentEssayResult?.student_feedback && (
                      <div className="mt-2 flex justify-between items-center">
                        <Button
                          onClick={() =>
                            handleUpdateSubmission(
                              submission.id!,
                              "feedback",
                              submission.studentEssayResult?.student_feedback ||
                                "",
                            )
                          }
                          size="sm"
                          rounded="lg"
                          bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                          textColor="text-blue-700 dark:text-blue-300"
                          className="text-xs"
                        >
                          {t("essayAssignments.useAISuggestion") ||
                            "AI Önerisini Kullan"}
                        </Button>
                        {submission.studentEssayResult?.score !== undefined &&
                          hasValidMaxScore && (
                            <Button
                              onClick={() =>
                                handleUpdateSubmission(
                                  submission.id!,
                                  "score",
                                  submission.studentEssayResult?.score || 0,
                                )
                              }
                              size="sm"
                              rounded="lg"
                              bgColor="bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800"
                              textColor="text-green-700 dark:text-green-300"
                              className="text-xs"
                            >
                              {t("essayAssignments.useAIScore") ||
                                "AI Puanını Kullan"}{" "}
                              ({submission.studentEssayResult.score}/{maxScore})
                            </Button>
                          )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <Button
            onClick={onClose}
            size="md"
            rounded="lg"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-gray-700 dark:text-gray-200"
            borderWidth="thin"
            borderColor="border-gray-200 dark:border-gray-700"
          >
            {t("common.cancel") || "İptal"}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="md"
            rounded="lg"
            bgColor="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
            textColor="text-white"
            className="flex items-center"
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                {t("common.saving") || "Kaydediliyor"}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t("essayAssignments.bulkGrading.saveAll") || "Hepsini Kaydet"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Enhanced RowDetailAccordion with better scoring support
interface RowDetailAccordionProps {
  submission: StudentEssaySubmissionResponseDto;
  editData: { feedback: string; score: number };
  onDataChange: (field: "feedback" | "score", value: string | number) => void;
  onSave: () => void;
  onCancel: () => void;
  defaultMaxScore?: number;
  isReadOnly?: boolean; // ADDED: New prop to control read-only state
}

const RowDetailAccordion: React.FC<RowDetailAccordionProps> = ({
  submission,
  editData,
  onDataChange,
  onSave,
  onCancel,
  defaultMaxScore,
  isReadOnly = false, // ADDED: Default to false for backward compatibility
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  const maxScore = getMaxScoreForSubmission(submission, defaultMaxScore);
  const hasValidMaxScore = maxScore > 0;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  const radarData = useMemo(() => {
    return getDynamicRadarData(submission);
  }, [submission]);

  const showRadarChart = shouldShowRadarChart(radarData);

  const getPerformanceLevel = (score: number, maxScore: number) => {
    if (maxScore === 0) return { label: "-", color: "text-gray-500" };

    const percentage = (score / maxScore) * 100;
    if (percentage >= 90)
      return {
        label: t("essayAssignments.performance.excellent") || "Mükemmel",
        color: "text-green-600",
      };
    if (percentage >= 80)
      return {
        label: t("essayAssignments.performance.veryGood") || "Çok İyi",
        color: "text-blue-600",
      };
    if (percentage >= 70)
      return {
        label: t("essayAssignments.performance.good") || "İyi",
        color: "text-yellow-600",
      };
    if (percentage >= 60)
      return {
        label: t("essayAssignments.performance.average") || "Orta",
        color: "text-orange-600",
      };
    return {
      label:
        t("essayAssignments.performance.needsImprovement") || "Geliştirilmeli",
      color: "text-red-600",
    };
  };

  const performanceLevel = getPerformanceLevel(editData.score, maxScore);
  const essayResult = submission.studentEssayResult;
  const hasAIAnalysis = essayResult && essayResult.criteria_evaluations;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
    >
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <Text variant="h3" className="text-lg font-semibold mb-2">
                  {hasAIAnalysis
                    ? t("essayAssignments.aiAnalysis.title") || "AI Analizi"
                    : t("essayAssignments.criteriaAnalysis") ||
                      "Kriter Analizi"}
                </Text>
              </div>
              {submission.essayFiles && submission.essayFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {submission.essayFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <span
                          className="block text-sm text-gray-800 dark:text-gray-200 truncate max-w-[180px]"
                          title={file.fileName}
                        >
                          {file.fileName || `Dosya ${index + 1}`}
                        </span>
                      </div>
                      <Button
                        onClick={() => {
                          if (file.downloadUrl) {
                            window.open(
                              file.downloadUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }
                        }}
                        size="sm"
                        rounded="lg"
                        bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                        textColor="text-blue-700 dark:text-blue-300"
                        className="ml-3 flex items-center text-xs shrink-0"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {t("common.download") || "İndir"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <div className="text-sm text-gray-500">
                    {hasAIAnalysis && essayResult?.overall_summary && (
                      <div className="mt-2 text-xs text-left">
                        <Text variant="p" className="font-medium mb-1">
                          {t("essayAssignments.aiAnalysis.summary") || "Özet"}:
                        </Text>
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          className="text-gray-600 dark:text-gray-400"
                        >
                          {essayResult.overall_summary}
                        </Markdown>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Conditionally render radar chart */}
            {showRadarChart && hasValidMaxScore && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fontSize: 10 }}
                      className="text-xs"
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[
                        0,
                        Math.max(...radarData.map((d) => d.fullMark || 0)),
                      ]}
                      tick={{ fontSize: 10 }}
                      tickCount={6}
                    />
                    <Radar
                      name={t("essayAssignments.score.title") || "Puan"}
                      dataKey="A"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-4 space-y-2">
              <Text variant="h4" className="text-sm font-semibold mb-2">
                {hasAIAnalysis
                  ? t("essayAssignments.aiAnalysis.criteriaBreakdown") ||
                    "AI Kriter Dağılımı"
                  : t("essayAssignments.criteriaBreakdown") ||
                    "Kriter Dağılımı"}
              </Text>
              {hasValidMaxScore ? (
                radarData.map((criterion, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-gray-600 dark:text-gray-400 flex-1 mr-2">
                      {criterion.subject}:
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{
                            width: `${(criterion.A / criterion.fullMark) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-blue-600 font-medium w-8">
                        {criterion.A}/{criterion.fullMark}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <Text variant="p" className="text-sm">
                    {t("essayAssignments.noMaxScore") ||
                      "Maksimum puan bulunamadı"}
                  </Text>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* MODIFIED: Score slider - show read-only version when isReadOnly is true */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <Text variant="h4" className="text-lg font-semibold mb-3">
                {t("essayAssignments.individualScore") || "Bireysel Puan"}
              </Text>
              <div className="space-y-3">
                {hasValidMaxScore ? (
                  <div className="flex items-center gap-4">
                    {isReadOnly ? (
                      // ADDED: Read-only score display
                      <div className="flex-1 flex items-center justify-between">
                        <div className="bg-blue-100 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
                          <Text
                            variant="span"
                            className="text-sm font-bold text-blue-800 dark:text-blue-200"
                          >
                            {editData.score}/{maxScore}
                          </Text>
                        </div>
                        <Text
                          variant="span"
                          className={`text-sm font-medium ${performanceLevel.color}`}
                        >
                          ({performanceLevel.label})
                        </Text>
                      </div>
                    ) : (
                      // EXISTING: Editable score slider
                      <>
                        <input
                          type="range"
                          min={0}
                          max={maxScore}
                          value={editData.score}
                          onChange={(e) =>
                            onDataChange("score", Number(e.target.value))
                          }
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        />
                        <span className="text-sm font-medium text-blue-600 min-w-[60px]">
                          {editData.score}/{maxScore}
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Text variant="p" className="text-sm">
                      {t("essayAssignments.noMaxScore") ||
                        "Maksimum puan bulunamadı"}
                    </Text>
                  </div>
                )}

                {hasAIAnalysis &&
                  essayResult &&
                  hasValidMaxScore &&
                  !isReadOnly && (
                    <div className="text-xs text-gray-500 space-y-1">
                      {essayResult.score !== undefined && (
                        <Button
                          onClick={() =>
                            onDataChange("score", essayResult.score || 0)
                          }
                          size="sm"
                          rounded="lg"
                          bgColor="bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800"
                          textColor="text-green-700 dark:text-green-300"
                          className="text-xs"
                        >
                          {t("essayAssignments.useAIScore") ||
                            "AI Puanını Kullan"}{" "}
                          ({essayResult.score}/
                          {essayResult.max_score || maxScore})
                        </Button>
                      )}
                    </div>
                  )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <Text variant="h4" className="text-lg font-semibold mb-3">
                {t("essayAssignments.feedback.title") || "Geri Bildirim"}
              </Text>
              {isReadOnly ? (
                // ADDED: Read-only feedback display
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Text variant="p" className="text-sm whitespace-pre-wrap">
                    {editData.feedback ||
                      t("essayAssignments.feedback.noFeedback") ||
                      "Geri bildirim yok"}
                  </Text>
                </div>
              ) : (
                // EXISTING: Editable feedback textarea
                <>
                  <textarea
                    value={editData.feedback}
                    onChange={(e) => onDataChange("feedback", e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={6}
                    placeholder={
                      hasAIAnalysis && essayResult?.student_feedback
                        ? `${t("essayAssignments.aiAnalysis.suggestion") || "AI Önerisi"}: ${essayResult.student_feedback.substring(0, 50)}...`
                        : t("essayAssignments.feedback.placeholder") ||
                          "Öğrenci için geri bildirim girin..."
                    }
                  />
                  {hasAIAnalysis && essayResult?.student_feedback && (
                    <Button
                      onClick={() =>
                        onDataChange(
                          "feedback",
                          essayResult.student_feedback || "",
                        )
                      }
                      size="sm"
                      rounded="lg"
                      bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                      textColor="text-blue-700 dark:text-blue-300"
                      className="mt-2 text-xs"
                    >
                      {t("essayAssignments.useAISuggestion") ||
                        "AI Önerisini Kullan"}
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* MODIFIED: Only show action buttons when not read-only */}
            {!isReadOnly && (
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  onClick={onCancel}
                  size="md"
                  rounded="lg"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  textColor="text-gray-700 dark:text-gray-200"
                  borderWidth="thin"
                  borderColor="border-gray-200 dark:border-gray-700"
                  disabled={isSaving}
                >
                  {t("common.cancel") || "İptal"}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  size="md"
                  rounded="lg"
                  bgColor="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                  textColor="text-white"
                  className="flex items-center"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      {t("common.saving") || "Kaydediliyor..."}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t("essayAssignments.saveAndGrade") ||
                        "Kaydet ve Değerlendir"}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Student Assignment Modal
interface StudentAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignmentId: number;
  onAssignmentComplete: () => void;
}

const StudentAssignmentModal: React.FC<StudentAssignmentModalProps> = ({
  isOpen,
  onClose,
  assignmentId,
  onAssignmentComplete,
}) => {
  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { mutateAsync: assignStudentsToEssayAssignment } = useAssignStudents();

  const handleAssignStudents = async () => {
    if (selectedStudents.length === 0) {
      addToast({
        title:
          t("essayAssignments.error.noStudentsSelected") || "Öğrenci Seçilmedi",
        description:
          t("essayAssignments.error.selectStudents") || "Lütfen öğrenci seçin",
        variant: "error",
        duration: 3000,
      });
      return;
    }

    setIsAssigning(true);
    try {
      await assignStudentsToEssayAssignment({
        id: assignmentId,
        data: {
          items: selectedStudents,
        },
      });

      addToast({
        title: t("essayAssignments.assignment.success") || "Atama Başarılı",
        description:
          t("essayAssignments.assignment.successDescription") ||
          "Öğrenciler başarıyla atandı",
        variant: "success",
        duration: 3000,
      });

      onAssignmentComplete();
      onClose();
    } catch (error) {
      console.error("Error assigning students:", error);
      addToast({
        title: t("essayAssignments.assignment.error") || "Atama Hatası",
        description:
          t("essayAssignments.assignment.errorDescription") ||
          "Öğrenci atama başarısız",
        variant: "error",
        duration: 5000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Text variant="h2" className="text-xl font-semibold">
              {t("essayAssignments.assignStudents") || "Öğrenciye Ata"}
            </Text>
            <Button
              onClick={onClose}
              size="sm"
              rounded="lg"
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              textColor="text-gray-500 dark:text-gray-400"
            >
              <XCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-96">
          <ClassroomAndStudentSelector
            selectedStudents={selectedStudents}
            setSelectedStudents={setSelectedStudents}
            assignedStudentIds={[]}
            shouldUseModal={false}
          />
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <Button
            onClick={onClose}
            size="md"
            rounded="lg"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-gray-700 dark:text-gray-200"
            borderWidth="thin"
            borderColor="border-gray-200 dark:border-gray-700"
          >
            {t("common.cancel") || "İptal"}
          </Button>
          <Button
            onClick={handleAssignStudents}
            disabled={isAssigning || selectedStudents.length === 0}
            size="md"
            rounded="lg"
            bgColor="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
            textColor="text-white"
            className="flex items-center"
          >
            {isAssigning ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                {t("common.saving") || "Kaydediliyor"}
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {t("essayAssignments.assignStudents") || "Öğrenciye Ata"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const EssayAssignmentsDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();

  // State management
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [showBulkGradingModal, setShowBulkGradingModal] = useState(false);
  const [showStudentAssignmentModal, setShowStudentAssignmentModal] =
    useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [selectedSubmission, setSelectedSubmission] =
    useState<StudentEssaySubmissionResponseDto | null>(null);
  const [selectedEssayGrader, setSelectedEssayGrader] = useState<number | null>(
    null,
  );
  const [feedbackData] = useState<{ [key: number]: string }>({});
  const [rowEditData, setRowEditData] = useState<{
    [key: number]: { feedback: string; score: number };
  }>({});
  // ADDED: State to track which submission is being shared
  const [sharingSubmissionId, setSharingSubmissionId] = useState<number | null>(
    null,
  );

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // API hooks
  const {
    data: assignment,
    isLoading: assignmentLoading,
    error: assignmentError,
    refetch: refetchAssignment,
  } = useGetEssayAssignment(Number(id), {
    query: {
      enabled: !!id,
      retry: 3,
      retryDelay: 1000,
    },
  });

  const {
    data: submissionsResponse,
    isLoading: submissionsLoading,
    error: submissionsError,
    refetch: refetchSubmissions,
  } = useGetSubmissionsForAssignment(Number(id), undefined, {
    query: {
      enabled: !!id,
      retry: 3,
      retryDelay: 1000,
      // FIXED: Proper polling implementation for AI grading
      refetchInterval: (data) => {
        const submissions = data.state.data?.submissions || [];
        const hasAIGrading = submissions.some(
          (submission: StudentEssaySubmissionResponseDto) =>
            submission.studentEssayResult === null &&
            submission.status === "SUBMITTED" &&
            submission.essayFiles &&
            submission.essayFiles.length > 0,
        );
        return hasAIGrading ? 5000 : false; // Poll every 5 seconds if AI grading is in progress
      },
    },
  });

  const { data: essayGraderData } = useGetEssayGrader(selectedEssayGrader!, {
    query: {
      enabled: !!selectedEssayGrader,
    },
  });

  const { mutateAsync: updateGrade } = useUpdateGrade();
  const { mutateAsync: bulkUpdateGrades } = useBulkUpdateGrades();
  const { mutateAsync: releaseGrades } = useReleaseGrades();

  // Loading states
  const isInitialLoading = assignmentLoading || submissionsLoading;
  const hasError = assignmentError || submissionsError;

  // Extract submissions from response
  const submissions =
    submissionsResponse?.submissions || assignment?.submissions || [];

  // FIXED: Get default assignment max score from submissions - don't default to 100
  const getDefaultAssignmentMaxScore = () => {
    // Try to find maxScore from any submission that has it
    const submissionWithMaxScore = submissions?.find(
      (s) => s.maxScore && s.maxScore > 0,
    );
    if (submissionWithMaxScore?.maxScore) {
      return submissionWithMaxScore.maxScore;
    }

    // Try to find maxScore from studentEssayResult
    const submissionWithEssayResult = submissions?.find(
      (s) =>
        s.studentEssayResult?.max_score && s.studentEssayResult.max_score > 0,
    );
    if (submissionWithEssayResult?.studentEssayResult?.max_score) {
      return submissionWithEssayResult.studentEssayResult.max_score;
    }

    // Try to calculate from criteria_evaluations
    const submissionWithCriteria = submissions?.find(
      (s) =>
        s.studentEssayResult?.criteria_evaluations &&
        s.studentEssayResult.criteria_evaluations.length > 0,
    );
    if (submissionWithCriteria?.studentEssayResult?.criteria_evaluations) {
      const totalMaxScore =
        submissionWithCriteria.studentEssayResult.criteria_evaluations.reduce(
          (sum, criterion) => {
            return sum + (criterion.max_score || 0);
          },
          0,
        );
      if (totalMaxScore > 0) {
        return totalMaxScore;
      }
    }

    // FIXED: Return 0 instead of 100 as default fallback
    return 0;
  };

  // Filter submissions based on search and filters
  const filteredSubmissions = useMemo(() => {
    if (!submissions) return [];

    const dueDatePassed = isDueDatePassed(assignment?.dueDate);

    return submissions.filter((submission) => {
      const searchMatch =
        !searchQuery ||
        submission.studentName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        submission.studentId?.toString().includes(searchQuery);

      const statusInfo = getSubmissionStatus(submission, t, dueDatePassed);
      const statusMatch =
        selectedStatus === "all" || statusInfo.status === selectedStatus;

      return searchMatch && statusMatch;
    });
  }, [submissions, searchQuery, selectedStatus, t, assignment?.dueDate]);

  // Handle individual grade update
  const handleGradeUpdate = useCallback(
    async (submissionId: number, data: UpdateGradeRequestDto) => {
      try {
        await updateGrade({
          submissionId: submissionId,
          data: data,
        });

        addToast({
          title: t("essayAssignments.feedback.saved") || "Kaydedildi",
          description:
            t("essayAssignments.feedback.savedDescription") ||
            "Geri bildirim başarıyla kaydedildi",
          variant: "success",
          duration: 3000,
        });

        await Promise.all([refetchAssignment(), refetchSubmissions()]);
      } catch (error) {
        console.error("Error updating grade:", error);
        addToast({
          title: t("essayAssignments.feedback.error") || "Hata",
          description:
            t("essayAssignments.feedback.errorDescription") ||
            "Geri bildirim kaydedilemedi",
          variant: "error",
          duration: 5000,
        });
      }
    },
    [updateGrade, addToast, t, refetchAssignment, refetchSubmissions],
  );

  // Handle feedback save for individual submission
  const handleFeedbackSave = async (
    submissionId: number,
    feedback: string,
    score?: number,
  ) => {
    const updateData: UpdateGradeRequestDto = { feedback };
    if (score !== undefined) {
      updateData.score = score;
    }
    await handleGradeUpdate(submissionId, updateData);
  };

  // Handle score save for individual submission
  const handleScoreSave = async (submissionId: number, score: number) => {
    await handleGradeUpdate(submissionId, { score });
  };

  // Handle row detail save
  const handleRowDetailSave = async (submissionId: number) => {
    const editData = rowEditData[submissionId];
    if (!editData) return;

    try {
      await updateGrade({
        submissionId: submissionId,
        data: {
          feedback: editData.feedback,
          score: editData.score,
        },
      });

      addToast({
        title: t("essayAssignments.feedback.saved") || "Kaydedildi",
        description:
          t("essayAssignments.feedback.savedDescription") ||
          "Puan başarıyla kaydedildi",
        variant: "success",
        duration: 3000,
      });

      await Promise.all([refetchAssignment(), refetchSubmissions()]);
      setExpandedRowId(null);
    } catch (error) {
      console.error("Error updating grade:", error);
      addToast({
        title: t("essayAssignments.feedback.error") || "Hata",
        description:
          t("essayAssignments.feedback.errorDescription") ||
          "Puan kaydedilemedi",
        variant: "error",
        duration: 5000,
      });
    }
  };

  // Handle row data change
  const handleRowDataChange = (
    submissionId: number,
    field: "feedback" | "score",
    value: string | number,
  ) => {
    setRowEditData((prev) => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        [field]: value,
      },
    }));
  };

  // Handle bulk grade update
  const handleBulkGradeUpdate = async (updates: GradeUpdateItem[]) => {
    try {
      const requestData: BulkGradeUpdateRequestDto = {
        gradeUpdates: updates,
      };

      await bulkUpdateGrades({
        assignmentId: Number(id),
        data: requestData,
      });

      addToast({
        title: t("essayAssignments.bulkGrading.success") || "Başarılı",
        description:
          t("essayAssignments.bulkGrading.successDescription") ||
          "Toplu değerlendirme başarıyla tamamlandı",
        variant: "success",
        duration: 3000,
      });

      await Promise.all([refetchAssignment(), refetchSubmissions()]);
    } catch (error) {
      console.error("Error bulk updating grades:", error);
      addToast({
        title: t("essayAssignments.bulkGrading.error") || "Hata",
        description:
          t("essayAssignments.bulkGrading.errorDescription") ||
          "Toplu değerlendirme sırasında hata oluştu",
        variant: "error",
        duration: 5000,
      });
    }
  };

  // Handle share with student (GRADED -> GRADE_VISIBLE)
  const handleShareWithStudent = async (submissionId: number) => {
    // ADDED: Set loading state for this specific submission
    setSharingSubmissionId(submissionId);

    try {
      const submission = submissions?.find((s) => s.id === submissionId);
      if (!submission?.studentId) {
        addToast({
          title: t("essayAssignments.feedback.sendError") || "Hata",
          description: "Öğrenci ID bulunamadı",
          variant: "error",
          duration: 5000,
        });
        return;
      }

      const releaseGradesRequest: ReleaseGradesRequestDto = {
        studentIds: [submission.studentId],
      };

      await releaseGrades({
        id: Number(id),
        data: releaseGradesRequest,
      });

      addToast({
        title: t("essayAssignments.shareWithStudent.success") || "Başarılı",
        description:
          t("essayAssignments.shareWithStudent.successDescription") ||
          "Not öğrenci ile paylaşıldı",
        variant: "success",
        duration: 3000,
      });

      await Promise.all([refetchAssignment(), refetchSubmissions()]);
    } catch (error) {
      console.error("Error sharing with student:", error);
      addToast({
        title: t("essayAssignments.shareWithStudent.error") || "Hata",
        description:
          t("essayAssignments.shareWithStudent.errorDescription") ||
          "Not paylaşılırken hata oluştu",
        variant: "error",
        duration: 5000,
      });
    } finally {
      // ADDED: Clear loading state
      setSharingSubmissionId(null);
    }
  };

  // Handle review submission - now toggles accordion
  const handleReviewSubmission = (
    submission: StudentEssaySubmissionResponseDto,
  ) => {
    // Check if AI is currently grading
    const isAIGrading =
      submission.studentEssayResult === null &&
      submission.status === "SUBMITTED" &&
      submission.essayFiles &&
      submission.essayFiles.length > 0;

    if (isAIGrading) {
      addToast({
        title: t("essayAssignments.aiGrading.inProgress") || "AI Analiz Ediyor",
        description:
          t("essayAssignments.aiGrading.pleaseWait") ||
          "Lütfen AI analizi tamamlanana kadar bekleyin",
        variant: "info",
        duration: 5000,
      });
      return;
    }

    if (
      submission.status === "SUBMITTED" ||
      submission.status === "GRADED" ||
      submission.status === "GRADE_VISIBLE"
    ) {
      if (expandedRowId === submission.id) {
        setExpandedRowId(null);
      } else {
        setExpandedRowId(submission.id!);
        if (!rowEditData[submission.id!]) {
          setRowEditData((prev) => ({
            ...prev,
            [submission.id!]: {
              feedback:
                submission.feedback ||
                submission.studentEssayResult?.student_feedback ||
                "",
              score:
                submission.score || submission.studentEssayResult?.score || 0,
            },
          }));
        }
      }
    } else if (submission.id) {
      setSelectedEssayGrader(submission.id);
      setSelectedSubmission(submission);
    }
  };

  // Handle row cancel
  const handleRowCancel = (submissionId: number) => {
    const submission = submissions?.find((s) => s.id === submissionId);
    if (submission) {
      setRowEditData((prev) => ({
        ...prev,
        [submissionId]: {
          feedback:
            submission.feedback ||
            submission.studentEssayResult?.student_feedback ||
            "",
          score: submission.score || submission.studentEssayResult?.score || 0,
        },
      }));
    }
    setExpandedRowId(null);
  };

  // Handle assignment complete
  const handleAssignmentComplete = async () => {
    await Promise.all([refetchAssignment(), refetchSubmissions()]);
  };

  // FIXED: Format submission date - new format like "12:00 20 Mart"
  const formatSubmissionDate = (date: string) => {
    const dateObj = new Date(date);
    const timeString = dateObj.toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const dateString = dateObj.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
    });
    return `${timeString} ${dateString}`;
  };

  // FIXED: Format date to show only day and month like "17 Mart"
  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
    });
  };

  // Show loading screen during initial load
  if (isInitialLoading) {
    return <EssayDetailLoader />;
  }

  // Show error state
  if (hasError || !assignment) {
    return (
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="text-center py-12">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <Text variant="h2" className="text-xl font-semibold mb-2">
            {hasError
              ? t("essayAssignments.error.loadFailed") ||
                "Kompozisyon yüklenemedi"
              : t("essayAssignments.notFound") || "Kompozisyon bulunamadı"}
          </Text>
          <Text variant="p" className="text-gray-500 dark:text-gray-400 mb-4">
            {hasError
              ? t("essayAssignments.error.tryAgain") || "Lütfen tekrar deneyin"
              : t("essayAssignments.error.notFoundDescription") ||
                "Aradığınız kompozisyon mevcut değil"}
          </Text>
          <div className="flex justify-center gap-3">
            <Button
              onClick={() => navigate(-1)}
              className="flex items-center"
              bgColor="bg-gray-600 hover:bg-gray-700"
              textColor="text-white"
              size="md"
              rounded="lg"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t("common.back") || "Geri"}
            </Button>
            {hasError && (
              <Button
                onClick={() => {
                  refetchAssignment();
                  refetchSubmissions();
                }}
                className="flex items-center"
                bgColor="bg-blue-600 hover:bg-blue-700"
                textColor="text-white"
                size="md"
                rounded="lg"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("common.retry") || "Tekrar Dene"}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render essay analysis if available
  const renderEssayAnalysis = () => {
    if (!essayGraderData) {
      return (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-center py-12">
            <InlineLoader size={24} />
            <Text variant="p" className="ml-3 text-gray-500">
              {t("essayAssignments.analysis.loading") || "Analiz yükleniyor..."}
            </Text>
          </div>
        </div>
      );
    }

    const submissionResult = essayGraderData.results?.find(
      (result) => result.id === selectedSubmission?.id,
    );

    if (!submissionResult?.result) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <Text variant="h2" size="xl" className="font-semibold">
            {t("essayAssignments.analysis.results") || "Analiz Sonuçları"}
          </Text>
          <Button
            onClick={() => {
              setSelectedEssayGrader(null);
              setSelectedSubmission(null);
            }}
            size="sm"
            rounded="lg"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
            textColor="text-gray-700 dark:text-gray-200"
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t("common.back") || "Geri"}
          </Button>
        </div>

        <ChatMessageBox
          message={submissionResult.result}
          t={t}
          onNavigate={() => {}}
        />
      </motion.div>
    );
  };

  // Calculate statistics
  const totalStudents = submissions?.length || 0;
  const submittedCount =
    submissions?.filter(
      (s) =>
        s.status === "SUBMITTED" ||
        s.status === "GRADED" ||
        s.status === "GRADE_VISIBLE",
    )?.length || 0;
  const pendingCount =
    submissions?.filter((s) => s.status === "NOT_SUBMITTED")?.length || 0;
  const gradedCount =
    submissions?.filter(
      (s) => s.status === "GRADED" || s.status === "GRADE_VISIBLE",
    )?.length || 0;
  const aiGradingCount =
    submissions?.filter(
      (s) =>
        s.studentEssayResult === null &&
        s.status === "SUBMITTED" &&
        s.essayFiles &&
        s.essayFiles.length > 0,
    )?.length || 0;
  const defaultMaxScore = getDefaultAssignmentMaxScore();

  // Check if due date has passed
  const dueDatePassed = isDueDatePassed(assignment.dueDate);

  // MODIFIED: Helper function to determine if submission is editable
  const isSubmissionEditable = (
    submission: StudentEssaySubmissionResponseDto,
  ) => {
    return (
      (submission.status === "SUBMITTED" || submission.status === "GRADED") &&
      submission.studentEssayResult !== null
    );
  };

  // MODIFIED: Helper function to determine if submission actions should be shown
  const getSubmissionActions = (
    submission: StudentEssaySubmissionResponseDto,
  ) => {
    const isAIGrading =
      submission.studentEssayResult === null &&
      submission.status === "SUBMITTED" &&
      submission.essayFiles &&
      submission.essayFiles.length > 0;

    const canReview =
      submission.status !== "NOT_SUBMITTED" &&
      (submission.status === "SUBMITTED" ||
        submission.status === "GRADED" ||
        submission.status === "GRADE_VISIBLE");

    const canShareWithStudent =
      (submission.status === "SUBMITTED" &&
        submission.studentEssayResult !== null) ||
      submission.status === "GRADED";

    return {
      canReview: canReview && !isAIGrading,
      canShareWithStudent,
      isAIGrading,
    };
  };

  // Render submissions table
  const renderSubmissionsTable = () => {
    if (!submissions || submissions.length === 0) {
      return (
        <div className="mt-8 text-center text-gray-500 dark:text-gray-400">
          <Text variant="h3" className="mb-2">
            {t("essayAssignments.submissions.noSubmissions") ||
              "Henüz kompozisyon yok"}
          </Text>
          <Text variant="p" status="secondary">
            {t("essayAssignments.submissions.noSubmissionsDescription") ||
              "Öğrenciler henüz kompozisyonlarını teslim etmedi"}
          </Text>
        </div>
      );
    }

    return (
      <div className="w-full">
        {/* Search and Filter Controls */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={
                  t("essayAssignments.search.placeholder") || "Öğrenci ara..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]"
            >
              <option value="all">
                {t("essayAssignments.filters.allStatuses") || "Tüm Durumlar"}
              </option>
              <option value="NOT_SUBMITTED">
                {t("essayAssignments.status.notSubmitted") || "Teslim Edilmedi"}
              </option>
              <option value="SUBMITTED">
                {t("essayAssignments.status.submitted") || "Teslim Edildi"}
              </option>
              <option value="AI_GRADING">
                {t("essayAssignments.status.aiGrading") || "AI Analiz Ediyor"}
              </option>
              <option value="GRADED">
                {t("essayAssignments.status.graded") || "Değerlendirildi"}
              </option>
              <option value="GRADE_VISIBLE">
                {t("essayAssignments.status.gradeVisible") || "Not Görünür"}
              </option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => setShowBulkGradingModal(true)}
              disabled={
                filteredSubmissions.filter(
                  (s) =>
                    (s.status === "SUBMITTED" || s.status === "GRADED") &&
                    s.studentEssayResult !== null,
                ).length === 0
              }
              className="flex items-center"
              bgColor="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400"
              textColor="text-white"
              size="sm"
              rounded="lg"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              {t("essayAssignments.bulkGrading.title") ||
                "Toplu Değerlendirme"}{" "}
              (
              {
                filteredSubmissions.filter(
                  (s) =>
                    (s.status === "SUBMITTED" || s.status === "GRADED") &&
                    s.studentEssayResult !== null,
                ).length
              }
              )
            </Button>

            <Button
              onClick={() => setShowFeedbackSection(!showFeedbackSection)}
              className="flex items-center"
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              borderWidth={"thin"}
              borderColor="border-gray-200 dark:border-gray-700"
              textColor="text-gray-700 dark:text-gray-200"
              size="sm"
              rounded="lg"
            >
              <ChevronDown
                className={`h-4 w-4 mr-2 transition-transform ${
                  showFeedbackSection ? "rotate-180" : ""
                }`}
              />
              {showFeedbackSection
                ? t("essayAssignments.feedback.hideFeedback") ||
                  "Geri Bildirim Gizle"
                : t("essayAssignments.feedback.showFeedback") ||
                  "Geri Bildirim Göster"}
            </Button>
          </div>
        </div>

        {/* No filtered results message */}
        {filteredSubmissions.length === 0 &&
          (searchQuery || selectedStatus !== "all") && (
            <div className="mt-8 text-center text-gray-500 dark:text-gray-400">
              <Text variant="h3" className="mb-2">
                {t("essayAssignments.submissions.noFilteredSubmissions") ||
                  "Filtrelenen sonuç bulunamadı"}
              </Text>
              <Text variant="p" status="secondary">
                {t("essayAssignments.submissions.tryDifferentFilter") ||
                  "Farklı filtre deneyin"}
              </Text>
              <Button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedStatus("all");
                }}
                className="mt-4"
                bgColor="bg-blue-600 hover:bg-blue-700"
                textColor="text-white"
                size="sm"
                rounded="lg"
              >
                {t("common.clearFilters") || "Filtreleri Temizle"}
              </Button>
            </div>
          )}

        {/* Desktop and Tablet View */}
        {filteredSubmissions.length > 0 && (
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("essayAssignments.student.name") || "Öğrenci Adı"}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("essayAssignments.status.title") || "Durum"}
                  </th>
                  {!showFeedbackSection && (
                    <>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        {t("essayAssignments.score.title") || "Puan"}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[160px]"
                      >
                        {t("essayAssignments.submission.date") ||
                          "Teslim Tarihi"}
                      </th>
                    </>
                  )}
                  {showFeedbackSection && (
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {t("essayAssignments.feedback.title") || "Geri Bildirim"}
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("essayAssignments.actions.title") || "İşlemler"}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSubmissions.map((submission) => {
                  const statusInfo = getSubmissionStatus(
                    submission,
                    t,
                    dueDatePassed,
                  );
                  const currentFeedback =
                    feedbackData[submission.id!] ??
                    submission.feedback ??
                    submission.studentEssayResult?.student_feedback ??
                    "";
                  const isEditable = isSubmissionEditable(submission);
                  const isGradeLocked = submission.status === "GRADE_VISIBLE";
                  const isExpanded = expandedRowId === submission.id;
                  const submissionMaxScore = getMaxScoreForSubmission(
                    submission,
                    defaultMaxScore,
                  );
                  const hasValidMaxScore = submissionMaxScore > 0;
                  const actions = getSubmissionActions(submission);

                  return (
                    <React.Fragment key={submission.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                  {(submission.studentName || "")
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {submission.studentName ||
                                  t("essayAssignments.student.unknown") ||
                                  "Bilinmeyen Öğrenci"}
                              </div>
                              {submission.studentEssayResult &&
                                hasValidMaxScore && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                                    {t("essayAssignments.aiAnalysis.title") ||
                                      "AI Analizi"}
                                    :{" "}
                                    {submission.studentEssayResult.score !==
                                      undefined &&
                                    submission.studentEssayResult.score !==
                                      null ? (
                                      `${submission.studentEssayResult.score}/${submission.studentEssayResult.max_score || submissionMaxScore}`
                                    ) : (
                                      <span className="text-gray-500">-</span>
                                    )}
                                  </div>
                                )}
                              {actions.isAIGrading && (
                                <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center">
                                  <Bot className="h-3 w-3 mr-1 animate-pulse" />
                                  {t(
                                    "essayAssignments.aiAnalysis.inProgress",
                                  ) || "AI analiz ediyor..."}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor} w-fit`}
                          >
                            {statusInfo.icon}
                            {statusInfo.label}
                          </div>
                        </td>
                        {!showFeedbackSection && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {!isGradeLocked && !actions.isAIGrading ? (
                                <ScoreSlider
                                  initialScore={
                                    submission.score ||
                                    submission.studentEssayResult?.score ||
                                    0
                                  }
                                  maxScore={submissionMaxScore}
                                  onSave={(score) =>
                                    handleScoreSave(submission.id!, score)
                                  }
                                  disabled={!isEditable}
                                />
                              ) : actions.isAIGrading ? (
                                <div className="flex items-center gap-2">
                                  <InlineLoader size={16} />
                                  <span className="text-sm text-gray-400">
                                    {t(
                                      "essayAssignments.aiAnalysis.calculating",
                                    ) || "Hesaplanıyor..."}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {hasValidMaxScore &&
                                  (submission.score || 0) > 0 ? (
                                    <div className="bg-blue-100 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                                      <Text
                                        variant="span"
                                        className="text-sm font-bold text-blue-800 dark:text-blue-200"
                                      >
                                        {submission.score || 0}/
                                        {submissionMaxScore}
                                      </Text>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-sm">
                                      -
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {submission.submittedAt ? (
                                formatSubmissionDate(submission.submittedAt)
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </>
                        )}
                        {showFeedbackSection && (
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
                            {!isGradeLocked && !actions.isAIGrading ? (
                              <FeedbackTextArea
                                initialFeedback={currentFeedback}
                                initialScore={
                                  submission.score ||
                                  submission.studentEssayResult?.score ||
                                  0
                                }
                                maxScore={submissionMaxScore}
                                onSave={(feedback, score) =>
                                  handleFeedbackSave(
                                    submission.id!,
                                    feedback,
                                    score,
                                  )
                                }
                                disabled={!isEditable}
                                showScoreInput={true}
                              />
                            ) : actions.isAIGrading ? (
                              <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                <InlineLoader size={16} />
                                <Text
                                  variant="p"
                                  className="text-sm text-orange-600 dark:text-orange-400"
                                >
                                  {t(
                                    "essayAssignments.aiAnalysis.generatingFeedback",
                                  ) || "Geri bildirim oluşturuluyor..."}
                                </Text>
                              </div>
                            ) : (
                              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <Text variant="p" className="text-sm">
                                  {submission.feedback ||
                                    t("essayAssignments.feedback.noFeedback") ||
                                    "Geri bildirim yok"}
                                </Text>
                                {hasValidMaxScore &&
                                  (submission.score || 0) > 0 && (
                                    <div className="mt-2">
                                      <div className="bg-blue-100 dark:bg-blue-900/20 px-2 py-1 rounded text-xs">
                                        <Text
                                          variant="span"
                                          className="font-bold text-blue-800 dark:text-blue-200"
                                        >
                                          {t("essayAssignments.score.title") ||
                                            "Puan"}
                                          : {submission.score || 0}/
                                          {submissionMaxScore}
                                        </Text>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <div className="flex justify-end gap-2">
                            {/* MODIFIED: Only show review button based on actions */}
                            {actions.canReview && (
                              <Button
                                onClick={() =>
                                  handleReviewSubmission(submission)
                                }
                                size="sm"
                                rounded="lg"
                                bgColor={
                                  isExpanded
                                    ? "bg-gray-600 hover:bg-gray-700"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }
                                textColor="text-white"
                                className="inline-flex items-center"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                {isExpanded
                                  ? t("common.close") || "Kapat"
                                  : t("essayAssignments.actions.review") ||
                                    "İncele"}
                              </Button>
                            )}
                            {/* MODIFIED: Only show share button when conditions are met */}
                            {actions.canShareWithStudent && (
                              <Button
                                onClick={() =>
                                  handleShareWithStudent(submission.id!)
                                }
                                size="sm"
                                rounded="lg"
                                bgColor="bg-green-600 hover:bg-green-700 disabled:bg-green-400"
                                textColor="text-white"
                                className="inline-flex items-center"
                                disabled={sharingSubmissionId === submission.id}
                              >
                                {sharingSubmissionId === submission.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    {t("common.sharing") || "Paylaşılıyor..."}
                                  </>
                                ) : (
                                  <>
                                    <Share2 className="h-4 w-4 mr-1" />
                                    {t(
                                      "essayAssignments.actions.shareWithStudent",
                                    ) || "Öğrenci ile Paylaş"}
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Accordion Row */}
                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan={showFeedbackSection ? 4 : 5}
                              className="p-0"
                            >
                              <RowDetailAccordion
                                submission={submission}
                                editData={
                                  rowEditData[submission.id!] || {
                                    feedback:
                                      submission.feedback ||
                                      submission.studentEssayResult
                                        ?.student_feedback ||
                                      "",
                                    score:
                                      submission.score ||
                                      submission.studentEssayResult?.score ||
                                      0,
                                  }
                                }
                                onDataChange={(field, value) =>
                                  handleRowDataChange(
                                    submission.id!,
                                    field,
                                    value,
                                  )
                                }
                                onSave={() =>
                                  handleRowDetailSave(submission.id!)
                                }
                                onCancel={() => handleRowCancel(submission.id!)}
                                defaultMaxScore={defaultMaxScore}
                                isReadOnly={
                                  submission.status === "GRADE_VISIBLE"
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View - Card-based layout */}
        {filteredSubmissions.length > 0 && (
          <div className="md:hidden space-y-4">
            {filteredSubmissions.map((submission) => {
              const statusInfo = getSubmissionStatus(
                submission,
                t,
                dueDatePassed,
              );
              const currentFeedback =
                feedbackData[submission.id!] ??
                submission.feedback ??
                submission.studentEssayResult?.student_feedback ??
                "";
              const isEditable = isSubmissionEditable(submission);
              const isGradeLocked = submission.status === "GRADE_VISIBLE";
              const isExpanded = expandedRowId === submission.id;
              const submissionMaxScore = getMaxScoreForSubmission(
                submission,
                defaultMaxScore,
              );
              const hasValidMaxScore = submissionMaxScore > 0;
              const actions = getSubmissionActions(submission);

              return (
                <div
                  key={submission.id}
                  className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
                >
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <div className="flex-shrink-0 h-10 w-10 mr-3">
                            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                {(submission.studentName || "")
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <Text variant="h4" className="text-sm font-medium">
                              {submission.studentName ||
                                t("essayAssignments.student.unknown") ||
                                "Bilinmeyen Öğrenci"}
                            </Text>
                            <Text
                              variant="p"
                              className="text-xs text-gray-500 dark:text-gray-400"
                            >
                              ID: {submission.studentId}
                            </Text>
                            {submission.studentEssayResult &&
                              hasValidMaxScore && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                                  {t("essayAssignments.aiAnalysis.title") ||
                                    "AI Analizi"}
                                  :{" "}
                                  {submission.studentEssayResult.score !==
                                    undefined &&
                                  submission.studentEssayResult.score !==
                                    null ? (
                                    `${submission.studentEssayResult.score}/${submission.studentEssayResult.max_score || submissionMaxScore}`
                                  ) : (
                                    <span className="text-gray-500">-</span>
                                  )}
                                </div>
                              )}
                            {actions.isAIGrading && (
                              <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center">
                                <Bot className="h-3 w-3 mr-1 animate-pulse" />
                                {t("essayAssignments.aiAnalysis.inProgress") ||
                                  "AI analiz ediyor..."}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusInfo.bgColor} ${statusInfo.textColor}`}
                          >
                            {statusInfo.icon}
                            {statusInfo.label}
                          </div>
                          {!isGradeLocked && !actions.isAIGrading ? (
                            <ScoreSlider
                              initialScore={
                                submission.score ||
                                submission.studentEssayResult?.score ||
                                0
                              }
                              maxScore={submissionMaxScore}
                              onSave={(score) =>
                                handleScoreSave(submission.id!, score)
                              }
                              disabled={!isEditable}
                            />
                          ) : actions.isAIGrading ? (
                            <div className="flex items-center gap-2">
                              <InlineLoader size={16} />
                              <span className="text-sm text-gray-400">
                                {t("essayAssignments.aiAnalysis.calculating") ||
                                  "Hesaplanıyor..."}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {hasValidMaxScore &&
                              (submission.score || 0) > 0 ? (
                                <div className="bg-blue-100 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                                  <Text
                                    variant="span"
                                    className="text-sm font-bold text-blue-800 dark:text-blue-200"
                                  >
                                    {submission.score || 0}/{submissionMaxScore}
                                  </Text>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Submission Date for Mobile */}
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {submission.submittedAt ? (
                            <>
                              <Clock className="h-3 w-3 inline mr-1" />
                              {t("essayAssignments.submission.date") ||
                                "Teslim"}
                              : {formatSubmissionDate(submission.submittedAt)}
                            </>
                          ) : (
                            <span className="text-gray-400">
                              {t("essayAssignments.notSubmitted") ||
                                "Henüz teslim edilmedi"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mobile Feedback Section */}
                    {showFeedbackSection && (
                      <div className="mt-4 space-y-3">
                        {!isGradeLocked && !actions.isAIGrading ? (
                          <FeedbackTextArea
                            initialFeedback={currentFeedback}
                            initialScore={
                              submission.score ||
                              submission.studentEssayResult?.score ||
                              0
                            }
                            maxScore={submissionMaxScore}
                            onSave={(feedback, score) =>
                              handleFeedbackSave(
                                submission.id!,
                                feedback,
                                score,
                              )
                            }
                            disabled={!isEditable}
                            showScoreInput={true}
                          />
                        ) : actions.isAIGrading ? (
                          <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                            <InlineLoader size={16} />
                            <Text
                              variant="p"
                              className="text-sm text-orange-600 dark:text-orange-400"
                            >
                              {t(
                                "essayAssignments.aiAnalysis.generatingFeedback",
                              ) || "Geri bildirim oluşturuluyor..."}
                            </Text>
                          </div>
                        ) : (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <Text variant="p" className="text-sm">
                              {submission.feedback ||
                                t("essayAssignments.feedback.noFeedback") ||
                                "Geri bildirim yok"}
                            </Text>
                            {hasValidMaxScore &&
                              (submission.score || 0) > 0 && (
                                <div className="mt-2">
                                  <div className="bg-blue-100 dark:bg-blue-900/20 px-2 py-1 rounded text-xs">
                                    <Text
                                      variant="span"
                                      className="font-bold text-blue-800 dark:text-blue-200"
                                    >
                                      {t("essayAssignments.score.title") ||
                                        "Puan"}
                                      : {submission.score || 0}/
                                      {submissionMaxScore}
                                    </Text>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!showFeedbackSection && (
                    <div className="p-4 flex justify-end gap-2">
                      {/* MODIFIED: Only show review button based on actions */}
                      {actions.canReview && (
                        <Button
                          onClick={() => handleReviewSubmission(submission)}
                          size="sm"
                          rounded="lg"
                          bgColor={
                            isExpanded
                              ? "bg-gray-600 hover:bg-gray-700"
                              : "bg-blue-600 hover:bg-blue-700"
                          }
                          textColor="text-white"
                          className="flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {isExpanded
                            ? t("common.close") || "Kapat"
                            : t("essayAssignments.actions.review") || "İncele"}
                        </Button>
                      )}
                      {/* MODIFIED: Only show share button when conditions are met */}
                      {actions.canShareWithStudent && (
                        <Button
                          onClick={() => handleShareWithStudent(submission.id!)}
                          size="sm"
                          rounded="lg"
                          bgColor="bg-green-600 hover:bg-green-700"
                          textColor="text-white"
                          className="flex items-center"
                        >
                          <Share2 className="h-4 w-4 mr-1" />
                          {t("essayAssignments.actions.shareWithStudent") ||
                            "Öğrenci ile Paylaş"}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Mobile Accordion */}
                  <AnimatePresence>
                    {isExpanded && (
                      <RowDetailAccordion
                        submission={submission}
                        editData={
                          rowEditData[submission.id!] || {
                            feedback:
                              submission.feedback ||
                              submission.studentEssayResult?.student_feedback ||
                              "",
                            score:
                              submission.score ||
                              submission.studentEssayResult?.score ||
                              0,
                          }
                        }
                        onDataChange={(field, value) =>
                          handleRowDataChange(submission.id!, field, value)
                        }
                        onSave={() => handleRowDetailSave(submission.id!)}
                        onCancel={() => handleRowCancel(submission.id!)}
                        defaultMaxScore={defaultMaxScore}
                        isReadOnly={submission.status === "GRADE_VISIBLE"}
                      />
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-4 max-w-7xl">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center mb-2">
              <Button
                onClick={() => navigate(-1)}
                className="mr-3"
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                borderWidth={"thin"}
                borderColor="border-gray-200 dark:border-gray-700"
                textColor="text-gray-700 dark:text-gray-200"
                size="sm"
                rounded="lg"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Text variant="h1" size="3xl" className="font-bold">
                {assignment.title}
              </Text>
            </div>
            <Text variant="p" status="secondary" className="mt-2 max-w-xl">
              {assignment.description}
            </Text>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg">
              <Calendar className="h-4 w-4 text-gray-500 mr-2" />
              <Text
                variant="span"
                className="text-gray-700 dark:text-gray-300 text-sm"
              >
                {t("essayAssignments.createdAt") || "Oluşturulma"}:{" "}
                {formatShortDate(assignment.createdAt)}
              </Text>
            </div>

            {/* Due Date - FIXED: Use formatShortDate */}
            {assignment.dueDate && (
              <div
                className={`flex items-center px-3 py-2 rounded-lg ${
                  dueDatePassed
                    ? "bg-red-50 dark:bg-red-900/20"
                    : "bg-amber-50 dark:bg-amber-900/20"
                }`}
              >
                <Clock
                  className={`h-4 w-4 mr-2 ${
                    dueDatePassed ? "text-red-500" : "text-amber-500"
                  }`}
                />
                <Text
                  variant="span"
                  className={`text-sm ${
                    dueDatePassed
                      ? "text-red-700 dark:text-red-300"
                      : "text-amber-700 dark:text-amber-300"
                  }`}
                >
                  {t("essayAssignments.dueDate") || "Teslim Tarihi"}:{" "}
                  {formatShortDate(assignment.dueDate)}{" "}
                  {assignment.dueDate &&
                    new Date(assignment.dueDate).toLocaleTimeString(
                      i18n.language,
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      },
                    )}
                  {dueDatePassed && (
                    <span className="ml-1 font-medium">
                      ({t("essayAssignments.overdue") || "Gecikmiş"})
                    </span>
                  )}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FIXED: Assignment Details Section - Show content and rubric */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Assignment Instructions */}
          <div>
            <Text variant="h4" className="font-semibold mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-blue-500" />
              {t("essayAssignments.assignmentInstructions") ||
                "Kompozisyon İçeriği"}
            </Text>
            {assignment.studentInstructionText ? (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Text variant="p" className="text-sm whitespace-pre-wrap">
                  {assignment.studentInstructionText}
                </Text>
              </div>
            ) : assignment.studentInstructionFile ? (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <Text variant="p" className="text-sm">
                    {assignment.studentInstructionFile.fileName}
                  </Text>
                  <Button
                    onClick={() => {
                      if (assignment.studentInstructionFile?.downloadUrl) {
                        window.open(
                          assignment.studentInstructionFile.downloadUrl,
                          "_blank",
                        );
                      }
                    }}
                    size="sm"
                    rounded="lg"
                    bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                    textColor="text-blue-700 dark:text-blue-300"
                    className="flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {t("common.view") || "Görüntüle"}
                  </Button>
                </div>
              </div>
            ) : (
              <Text variant="p" className="text-sm text-gray-500">
                {t("essayAssignments.noInstructions") || "Talimat sağlanmadı"}
              </Text>
            )}
          </div>

          {/* Rubric */}
          <div>
            <Text variant="h4" className="font-semibold mb-2 flex items-center">
              <BarChart4 className="h-4 w-4 mr-2 text-green-500" />
              {t("essayAssignments.rubric") || "Değerlendirme Kriterleri"}
            </Text>
            {assignment.rubricFile ? (
              <div className="px-2 py-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <Text variant="p" className="text-sm">
                    {assignment.rubricFile.fileName}
                  </Text>
                  <Button
                    onClick={() => {
                      if (assignment.rubricFile?.downloadUrl) {
                        window.open(
                          assignment.rubricFile.downloadUrl,
                          "_blank",
                        );
                      }
                    }}
                    size="sm"
                    rounded="lg"
                    bgColor="bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800"
                    textColor="text-green-700 dark:text-green-300"
                    className="flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {t("common.view") || "Görüntüle"}
                  </Button>
                </div>
              </div>
            ) : (
              <Text variant="p" className="text-sm text-gray-500">
                {t("essayAssignments.noRubric") || "Kriter sağlanmadı"}
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <Text variant="h3" className="text-lg font-semibold">
                {totalStudents}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t("essayAssignments.stats.totalStudents") || "Toplam Öğrenci"}
              </Text>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <Text variant="h3" className="text-lg font-semibold">
                {submittedCount}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t("essayAssignments.stats.submitted") || "Teslim Edildi"}
              </Text>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <Clock3 className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <Text variant="h3" className="text-lg font-semibold">
                {pendingCount}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t("essayAssignments.stats.pending") || "Beklemede"}
              </Text>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <Bot className="h-8 w-8 text-orange-500" />
            <div className="ml-3">
              <Text variant="h3" className="text-lg font-semibold">
                {aiGradingCount}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t("essayAssignments.stats.aiGrading") || "AI Analiz Ediyor"}
              </Text>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <Award className="h-8 w-8 text-purple-500" />
            <div className="ml-3">
              <Text variant="h3" className="text-lg font-semibold">
                {gradedCount}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t("essayAssignments.stats.graded") || "Değerlendirildi"}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BulkGradingModal
        isOpen={showBulkGradingModal}
        onClose={() => setShowBulkGradingModal(false)}
        submissions={filteredSubmissions.filter(
          (s) =>
            (s.status === "SUBMITTED" || s.status === "GRADED") &&
            s.studentEssayResult !== null,
        )}
        onBulkSave={handleBulkGradeUpdate}
        defaultMaxScore={defaultMaxScore}
      />

      <StudentAssignmentModal
        isOpen={showStudentAssignmentModal}
        onClose={() => setShowStudentAssignmentModal(false)}
        assignmentId={Number(id)}
        onAssignmentComplete={handleAssignmentComplete}
      />

      {/* Main Content */}
      <AnimatePresence mode="wait">
        {selectedEssayGrader ? (
          renderEssayAnalysis()
        ) : (
          <motion.div
            key="submissions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="mb-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <BarChart4 className="h-5 w-5 text-blue-500" />
                  <Text variant="h2" size="xl" className="font-semibold">
                    {t("essayAssignments.submissions.title") ||
                      "Öğrenci Kompozisyonları"}
                  </Text>
                </div>
                {/* FIXED: Replace "New Assignment" with "Assign Students" */}
                <Button
                  onClick={() => setShowStudentAssignmentModal(true)}
                  className="flex items-center"
                  bgColor="bg-blue-600 hover:bg-blue-700"
                  textColor="text-white"
                  size="md"
                  rounded="lg"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("essayAssignments.assignStudents") || "Öğrenciye Ata"}
                </Button>
              </div>
              <Text variant="p" status="secondary">
                {t("essayAssignments.submissions.description") ||
                  "Öğrenci kompozisyonlarını inceleyin ve değerlendirin"}
              </Text>
            </div>

            {renderSubmissionsTable()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EssayAssignmentsDetailView;
